import { useEffect, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Finds `selector` inside `wrapperRef` and re-finds it on every remount (the
 * library replaces the node wholesale on a Workbook `key` change) — same
 * MutationObserver-re-find pattern as `useSheetTabWheelScroll.ts`. Shared by
 * NameBox and StatusBar, both of which portal an app component into a
 * library-owned container.
 */
export const usePortalContainer = (
    wrapperRef: RefObject<HTMLElement>,
    selector: string,
    remountKey: string | number,
    workbookMounted: boolean,
): HTMLElement | null => {
    const [container, setContainer] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (!workbookMounted) {
            setContainer(null);
            return undefined;
        }
        const root = wrapperRef.current;
        if (!root) return undefined;

        const tryFind = (): boolean => {
            const found = root.querySelector<HTMLElement>(selector);
            if (found) setContainer(found);
            return Boolean(found);
        };
        if (tryFind()) return undefined;

        const observer = new MutationObserver(() => {
            if (tryFind()) observer.disconnect();
        });
        observer.observe(root, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [wrapperRef, selector, remountKey, workbookMounted]);

    return container;
};
